import { render } from "@testing-library/react";
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {normalizeDateInput} from "../utils/dateValidation";
import {
  getUnitPrefs,
  setUnitPrefs,
  cmFromFeetIn,
  kgFromLbs,
} from "../utils/unitConversion";

export default function Patient(props) {
  const fNameRef = useRef(null);
  const lNameRef = useRef(null);
  const genderRef = useRef(null);
  const dobRef = useRef(null);
  const pastMentalHealthConditionsRef = useRef(null);
  const pastFamilyMentalHealthConditionsRef = useRef(null);
  const pastHealthConditionsRef = useRef(null);
  const pastFamilyHealthConditionsRef = useRef(null);
  const medicationsRef = useRef(null);
  const addressRef = useRef(null);
  const cityRef = useRef(null);
  const stateRef = useRef(null);
  const phoneRef = useRef(null);
  const passwordRef = useRef(null);
  const confirmPasswordRef = useRef(null);

  const [dobError, setDobError] = useState("");
  const [heightUnit, setHeightUnit] = useState(() => getUnitPrefs().heightUnit);
  const [weightUnit, setWeightUnit] = useState(() => getUnitPrefs().weightUnit);
  const [heightFields, setHeightFields] = useState({cm: "", feet: "", inches: ""});
  const [weightField, setWeightField] = useState("");

  async function handleSubmit(e) {
    //validate if pwd and cpwd match first...& rest of fields are legit (not empty & correct format)
    e.preventDefault();

    const parsedDob = normalizeDateInput(dobRef.current.value);
    if (!parsedDob.isValid) {
      setDobError(parsedDob.error);
      return;
    }
    setDobError("");

    const heightMetric =
      heightUnit === "ft-in"
        ? cmFromFeetIn(heightFields.feet, heightFields.inches)
        : Number(heightFields.cm);
    const weightMetric = weightUnit === "lbs" ? kgFromLbs(weightField) : Number(weightField);

    function jwtDecode(t) {
      let token = {};
      token.raw = t;
      token.header = JSON.parse(window.atob(t.split(".")[0]));
      token.payload = JSON.parse(window.atob(t.split(".")[1]));
      return token;
    }

    const result = await fetch("https://authserver.brightlight.ai/registerPatient", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        pid: props.pid,
        fName: fNameRef.current.value,
        lName: lNameRef.current.value,
        gender: genderRef.current.value,
        dob: parsedDob.canonical,
        height: heightMetric,
        weight: weightMetric,
        pastMentalHealthConditions: pastMentalHealthConditionsRef.current.value,
        pastFamilyMentalHealthConditions: pastFamilyMentalHealthConditionsRef.current.value,
        pastHealthConditions: pastHealthConditionsRef.current.value,
        pastFamilyHealthConditions: pastFamilyHealthConditionsRef.current.value,
        medications: medicationsRef.current.value,
        address: addressRef.current.value,
        city: cityRef.current.value,
        state: stateRef.current.value,
        phone: phoneRef.current.value,
        password: passwordRef.current.value,
      }),
    }).then(async (res) => {
      if (res.status == 200) {
        const jwt = await res.json();
        window.location.href = window.location.origin + "/chatbot/" + jwtDecode(jwt).payload.pid;
      } else {
        console.log(res);
      }
    });
  }

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <input type="text" value="test@test.com" />
        <input type="text" ref={fNameRef} placeholder="first name" />
        <input type="text" ref={lNameRef} placeholder="last name" />
        <input type="text" ref={genderRef} placeholder="gender" />
        <input
          type="date"
          ref={dobRef}
          placeholder="dob (yyyy/mm/dd)"
          onChange={(e) => {
            const parsed = normalizeDateInput(e.target.value);
            setDobError(parsed.isValid ? "" : parsed.error);
          }}
        />
        {dobError && <p style={{color: "red", fontSize: "12px"}}>{dobError}</p>}

        <div style={{display: "flex", gap: "8px", alignItems: "center", marginTop: "8px"}}>
          <select
            value={heightUnit}
            onChange={(e) => {
              const unit = e.target.value;
              setHeightUnit(unit);
              setUnitPrefs({heightUnit: unit});
            }}
          >
            <option value="cm">cm</option>
            <option value="ft-in">ft / in</option>
          </select>
          {heightUnit === "cm" ? (
            <input
              type="number"
              placeholder="height (cm)"
              value={heightFields.cm}
              onChange={(e) => setHeightFields({...heightFields, cm: e.target.value})}
            />
          ) : (
            <>
              <input
                type="number"
                placeholder="ft"
                value={heightFields.feet}
                onChange={(e) => setHeightFields({...heightFields, feet: e.target.value})}
              />
              <input
                type="number"
                placeholder="in"
                value={heightFields.inches}
                onChange={(e) => setHeightFields({...heightFields, inches: e.target.value})}
              />
            </>
          )}
        </div>

        <div style={{display: "flex", gap: "8px", alignItems: "center", marginTop: "8px"}}>
          <select
            value={weightUnit}
            onChange={(e) => {
              const unit = e.target.value;
              setWeightUnit(unit);
              setUnitPrefs({weightUnit: unit});
            }}
          >
            <option value="kg">kg</option>
            <option value="lbs">lbs</option>
          </select>
          <input
            type="number"
            placeholder={weightUnit === "lbs" ? "weight (lbs)" : "weight (kg)"}
            value={weightField}
            onChange={(e) => setWeightField(e.target.value)}
          />
        </div>
        <input
          type="text"
          ref={pastMentalHealthConditionsRef}
          placeholder="past mental health conditions"
        />
        <input
          type="text"
          ref={pastFamilyMentalHealthConditionsRef}
          placeholder="past family mental health conditions"
        />
        <input type="text" ref={pastHealthConditionsRef} placeholder="past health conditions" />
        <input
          type="text"
          ref={pastFamilyHealthConditionsRef}
          placeholder="past family health conditions"
        />
        <input type="text" ref={medicationsRef} placeholder="medications" />
        <input type="text" ref={addressRef} placeholder="street address" />
        <input type="text" ref={cityRef} placeholder="city" />
        <input type="text" ref={stateRef} placeholder="state" />
        <input type="text" ref={phoneRef} placeholder="phone" />
        <input type="password" ref={passwordRef} placeholder="password" />
        <input type="password" ref={confirmPasswordRef} placeholder="confirm password" />
        <input type="submit" className="p-2.5" value="submit" />
      </form>
    </div>
  );
}
